<template>
  <div
    class="multiselect-container"
    :class="{error: error}"
    @keydown.esc.prevent="isOpen = false"
    v-click-outside="handleClickOutside"
  >
    <label :for="name">
      <slot />
      <transition name="showErrorIcon">
        <icon v-if="error" size="small" icon="error" />
      </transition>
    </label>
    <div class="input-container">
      <div v-if="isTaggable">
        <span class="tag" v-for="option in selected" :key="getTrackedOption(option)" @keydown.delete.enter="handleRemoveSelected(option)" tabindex="0">
          {{ getTrackedOptionLabel(option) }}
          <span class="tag-close"  @click="handleRemoveSelected(option)">&times;</span>
        </span>
      </div>
      <input
        ref="input"
        type="text"
        :placeholder="placeholder"
        :class="{ taggable: isTaggable }"
        :value="searchValue"
        @input="handleInput"
        @focus="handleFocus"
        @click="handleInputClick"
        @blur.self="handleBlur"
        @keydown.enter.self.prevent.stop="handleInputEnter"
        @keydown.tab.self.stop="resetInput"
        @keydown.down.stop.prevent="handleArrowKey(false)"
        @keydown.up.stop.prevent="handleArrowKey(true)"
      />
      <icon
        v-if="!isTaggable && hasOptions"
        icon="disclosure"
        size="small"
        @click.native="handleArrowClick"
        class="dropdownIcon"
        :class="{ rotated: showOptions }"
      />
      <ul v-show="showOptions" class="multiselect-options" ref="options" tabindex="-1">
        <li
          v-for="(option, index) in options"
          :key="getTrackedOption(option)"
          :class="{ selected: isSelected(option), highlighted: isHighlighted(index, option) }"
          @click="handleSelect(option)"
          @mousemove="handleOptionMouseMove(index)"
          @mousedown.prevent="handlePreventBlur"
        >{{ getTrackedOptionLabel(option) }}</li>
        <li v-if="!hasOptions && !canCreateOption">
          <slot name="noResult"></slot>
        </li>
        <li v-if="isLoading">
          <dh-loading-indicator v-if="isLoading" />
        </li>
      </ul>
      <p v-show="isAtMaxSelected && isActive">
        <slot name="limit-warning" />
      </p>
    </div>
    <div class="form-control-error">
      <slot name="error" />
    </div>
  </div>
</template>

<script>
const SINGLE_SELECT = 1
const NEGATIVE_POSITION = -1

export default {
  props: {
    options: {
      type: Array,
      default () {
        return []
      }
    },
    value: {
      type: [String, Object, Array],
      default () {
        return ''
      }
    },
    name: {
      type: String,
      required: true
    },
    placeholder: {
      type: String
    },
    error: {
      type: Boolean,
      default: false
    },
    trackBy: {
      type: String
    },
    optionLabel: {
      type: String
    },
    allowEmpty: {
      type: Boolean,
      default: true
    },
    startAt: {
      type: Number,
      default: 3
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    canCreateOption: {
      type: Boolean,
      default: false
    },
    isTaggable: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    maxSelected: {
      type: Number,
      default: SINGLE_SELECT
    }
  },
  data () {
    return {
      searchValue: '',
      pointerIndex: NEGATIVE_POSITION,
      isFocused: false,
      isOpen: false,
      isActive: false,
      selected: [],
      isScrolling: false
    }
  },
  computed: {
    hasOptions () {
      return this.options && this.options.length > 0
    },

    showOptions () {
      return this.isLoading || (this.isOpen && this.hasOptions)
    },

    pointerPosition () {
      return this.pointerIndex !== NEGATIVE_POSITION
        ? this.$refs.options.childNodes[this.pointerIndex].offsetTop
        : NEGATIVE_POSITION
    },

    pointerHeight () {
      return this.pointerIndex !== NEGATIVE_POSITION
        ? this.$refs.options.childNodes[this.pointerIndex].clientHeight
        : 0
    },

    isAtMaxSelected () {
      return this.selected.length === this.maxSelected
    },

    canBeClosed () {
      return !this.isScrolling && (!this.isTaggable
        || (this.isTaggable && this.isAtMaxSelected))
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (value) {
        if (this.isTaggable) {
          this.selected = this.value ? this.value : []
        } else {
          this.searchValue = this.getTrackedOptionLabel(value)
        }
      }
    },

    options () {
      this.resetOptionIndex()
    }
  },
  methods: {
    isHighlighted (index, option) {
      return index === this.pointerIndex && !this.isSelected(option)
    },

    isSelected (selected) {
      const trackedValue = this.getTrackedOption(selected)

      return this.selected.some(
        option => this.getTrackedOption(option) === trackedValue
      )
    },

    getTrackedOption (value) {
      return this.trackBy ? value[this.trackBy] : value
    },

    getTrackedOptionLabel (value) {
      return this.optionLabel ? value[this.optionLabel] : value
    },

    createTrackedOption (value) {
      return this.trackBy ? { [this.trackBy]: value } : value
    },

    handleInput (e) {
      if (this.isAtMaxSelected && this.isTaggable) return

      const value = e.target.value || ''

      if (!value && !this.isTaggable) {
        this.selected = []
        this.$emit('input', '')
      }

      this.isOpen = true
      this.searchValue = value
      this.handleSearch(value)
    },

    handleSearch (value = '') {
      this.$emit('search', value.length >= this.startAt ? value : '')
    },

    handleClickOutside (e) {
      this.isScrolling = false
      this.handleBlur(e)
    },

    // This trickery is to stop mobile devices from blurring when an option is selected
    handlePreventBlur () {
      return false
    },

    handleFocus () {
      if (this.isTaggable && this.isAtMaxSelected) {
        this.isActive = true
        return
      }
      this.$emit('focus')

      this.isFocused = true
      this.isActive = true
      this.isOpen = true
    },

    handleInputClick () {
      if (this.isAtMaxSelected) return
      this.isOpen = true
    },

    handleArrowClick () {
      this.isOpen = !this.isOpen

      if (this.showOptions) {
        this.$refs.input.focus()
      } else if (!this.isOpen) {
        this.handleBlur()
      }
    },

    handleBlur (e) {
      if (this.canCreateOption) {
        const trackedOption = this.createTrackedOption(this.searchValue)
        this.handleSelect(trackedOption)
      }

      this.isFocused = false

      if (e && e.relatedTarget === this.$refs.options) {
        this.isScrolling = true
      }

      this.resetInput()
      this.$emit('blur')
    },

    handleSelect (option) {
      if (this.isSelected(option)) {
        return
      }

      this.isScrolling = false

      if (this.maxSelected === SINGLE_SELECT) {
        this.selected = [option]
        this.searchValue = this.getTrackedOptionLabel(option)
        this.resetOptionIndex()
      } else if (this.selected.length < this.maxSelected) {
        this.selected = [...this.selected, option]
      } else {
        this.$emit('error', 'max.selected')
      }

      this.handleSave()
    },

    handleRemoveSelected (removeOption) {
      const trackedRemoveOption = this.getTrackedOption(removeOption)
      this.selected = this.selected
        .filter(option => this.getTrackedOption(option) !== trackedRemoveOption)

      this.handleSave()
    },

    handleSave () {
      const value = this.maxSelected === SINGLE_SELECT
        ? this.selected[0]
        : this.selected

      if (value !== this.value) {
        this.$emit('input', value)
      }

      this.resetInput()
    },

    handleInputEnter () {
      if (this.pointerIndex >= 0) {
        const value = this.options[this.pointerIndex]
        this.handleSelect(value)
      }

      if (this.canCreateOption) {
        this.$emit('input', this.createTrackedOption(this.searchValue))
        this.resetInput()
      }
    },

    handleOptionMouseMove (index) {
      this.pointerIndex = index
    },

    handleArrowKey (isUp) {
      if (!this.isOpen) {
        this.isOpen = true
        this.resetOptionIndex()
        return
      }

      this.pointerIndex = isUp
        ? Math.max(0, this.pointerIndex - 1)
        : Math.min(this.pointerIndex + 1, this.options.length - 1)

      // If new pointer position isn't fully within view, bring it in to view
      const currentScroll = this.$refs.options.scrollTop
      const dropdownHeight = this.$refs.options.clientHeight

      const bottomIsAbove = this.pointerPosition <= currentScroll
      const topIsBelow = (this.pointerPosition + this.pointerHeight)
        >= (currentScroll + dropdownHeight)

      if (bottomIsAbove || topIsBelow) {
        this.$refs.options.scrollTop = isUp
          ? this.pointerPosition
          : ((this.pointerPosition + this.pointerHeight) - dropdownHeight)
      }
    },

    resetOptionIndex () {
      this.pointerIndex = NEGATIVE_POSITION
    },

    resetInput () {
      if (!this.canCreateOption) {
        this.searchValue = this.isTaggable ? '' : this.getTrackedOptionLabel(this.value)
      }

      // Send an empty search to get initial option results
      this.handleSearch()
      this.resetOptionIndex()

      if (this.canBeClosed) {
        this.isOpen = false
      }

      if (!this.isFocused) {
        this.isActive = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$option-height: 40px;
$option-list-height: 200px;

.multiselect-container {
  position: relative;
}

.input-container {
  position: relative;
  margin-bottom: $d-xx-small;

  &.disabled {
    color: $deprecated-grey-medium-dark;
  }
}

input {
  @include transition-fast;
  @include label-med;
  @include deprecated-f-color-normal;

  @include breakpoint(medium) {
    @include label-med;
  }

  outline: none;
  border: none;
  padding: $d-xx-small $d-x-small;
  border-radius: 2px;
  width: 100%;
  padding: $d-small;
  margin-bottom: $d-tiny;
  background-color: rgba($color-white, 0);
  border-bottom: $line-medium;
  border-radius: 2px 2px 0 0;
  outline: 1px solid rgba($color-black, 0);
  outline-offset: 4px;

  &:active {
    padding-top: $d-small;
  }

  &:not(:read-only):focus {
    padding-top: $d-small;
    box-shadow: inset 0 -3px 0 $color-cream,
      inset 0 -100px 0 $color-cream-light;
    color: $color-black;
    background-color: rgba($color-white, 0);

    &::placeholder {
      color: $color-gray;
    }
  }

  &:disabled {
    color: $deprecated-grey-medium;
  }

  &:not(:read-only):focus {
    border-radius: 2px 2px 0 0;
  }

  &:disabled {
    cursor: not-allowed;
  }
}

label {
  @include font('040');
  display: block;
  text-transform: none;
  color: $color-black;
  margin-bottom: $d-xx-small;
}

.multiselect-options {
  border: 1px solid $deprecated-grey-light;

  list-style: none;
  padding: 0;
  margin: 0;
  max-height: $option-list-height;
  overflow: scroll;
  position: absolute;
  border-radius: 3px;
  z-index: 1;
  width: 100%;
  background: $color-white;

  li {
    @include font('040');

    padding: 10px 5px;

    &.selected {
      background: $deprecated-grey-x-med-light;
    }

    &.highlighted {
      background: $color-cream-light;
    }
  }
}

.tag {
  @include body-sml;

  position: relative;
  vertical-align: middle;
  display: inline-block;
  padding: 10px 34px 10px 18px;
  border-radius: 20px;
  min-width: 40px;
  margin: 0 10px 10px 0;
  padding-right: 30px;
  background: $color-black;
  color: $color-white;

  .tag-close {
    cursor: pointer;
    position: absolute;
    right: 13px;
    top: 9px;
    font-size: 1.2rem;
  }
}

.dropdownIcon {
  @include transition-fast;

  position: absolute;
  right: 0;
  top: 30%;

  &.rotated {
    transform: rotate(180deg);
  }
}

.form-control-feedback,
.form-control-info {
  @include body-sml;
  padding-top: 0;
}

.form-control-feedback {
  min-height: 1.6em;

  .error {
    color: $deprecated-color-ui-error;
  }
}

p {
  margin: 0;

  @include body-sml;
}
</style>
